import { Component, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { StrategyService } from '../../../../data/services/view/strategy.service';
import { CHANNEL_ICONS, FREQUENCY_CONFIG } from '../../../../data/constants/strategies.constants';

/**
 * Step 2: Configuración de Canales de Comunicación
 * ACTUALIZADO: Incluye frequency y frequencyInterval por canal
 */
@Component({
  selector: 'app-step2-channels',
  templateUrl: './step2-channels.component.html',
  styleUrls: ['./step2-channels.component.scss']
})
export class Step2ChannelsComponent implements OnInit {
  channelIcons = CHANNEL_ICONS;
  frequencyConfig = FREQUENCY_CONFIG;
  channels = ['email', 'sms', 'whatsapp', 'call'];
  channelLabels = {
    email: 'Email',
    sms: 'SMS',
    whatsapp: 'WhatsApp',
    call: 'Llamada Telefónica'
  };

  constructor(public strategyService: StrategyService) {}

  ngOnInit() {
    // Suscribirse a cambios para actualizar validaciones
    this.channelsForm.valueChanges.subscribe(() => {
      this.updateValidations();
    });
  }

  get form() {
    return this.strategyService.entity;
  }

  get channelsForm() {
    return this.form.get('channels') as FormGroup;
  }

  getChannelForm(channel: string): FormGroup {
    return this.channelsForm.get(channel) as FormGroup;
  }

  isChannelEnabled(channel: string): boolean {
    return this.getChannelForm(channel).get('enabled')?.value || false;
  }

  getChannelPriority(channel: string): number {
    return this.getChannelForm(channel).get('priority')?.value || 0;
  }

  getChannelFrequency(channel: string): number {
    return this.getChannelForm(channel).get('frequency')?.value || 1;
  }

  hasMultipleFrequency(channel: string): boolean {
    return this.getChannelFrequency(channel) > 1;
  }

  onChannelToggle(channel: string) {
    const channelForm = this.getChannelForm(channel);
    const enabled = channelForm.get('enabled')?.value;
    
    if (enabled) {
      // Si se activa, asignar masterTemplateId automáticamente
      const masterTemplateId = this.form.get('masterTemplateId')?.value;
      if (masterTemplateId) {
        channelForm.get('templateId')?.setValue(masterTemplateId);
      }
      
      // Asignar hora programada por defecto si no tiene una
      if (!channelForm.get('scheduledTime')?.value) {
        const priority = channelForm.get('priority')?.value || 1;
        const baseHour = 9; // Hora inicial: 09:00
        const hours = baseHour + (priority - 1);
        const timeString = `${hours.toString().padStart(2, '0')}:00`;
        channelForm.get('scheduledTime')?.setValue(timeString);
      }
      
      // Resetear frequency a valores por defecto
      channelForm.get('frequency')?.setValue(1);
      channelForm.get('frequencyInterval')?.setValue(FREQUENCY_CONFIG.DEFAULT_INTERVAL);
    } else {
      // Si se desactiva, limpiar scheduledTime y templateId
      channelForm.get('scheduledTime')?.setValue('');
      channelForm.get('templateId')?.setValue('');
      channelForm.get('frequency')?.setValue(1);
      channelForm.get('frequencyInterval')?.setValue(FREQUENCY_CONFIG.DEFAULT_INTERVAL);
    }
  }

  onFrequencyChange(channel: string) {
    const channelForm = this.getChannelForm(channel);
    const frequency = channelForm.get('frequency')?.value || 1;
    
    // Si frequency es 1, resetear interval
    if (frequency <= 1) {
      channelForm.get('frequencyInterval')?.setValue(FREQUENCY_CONFIG.DEFAULT_INTERVAL);
    }
  }

  updateValidations() {
    // Esta función puede usarse para validaciones adicionales en tiempo real
  }

  get hasEnabledChannel(): boolean {
    return this.channels.some(ch => this.isChannelEnabled(ch));
  }

  get validationMessage(): string {
    if (!this.hasEnabledChannel) {
      return 'Debe activar al menos un canal de comunicación';
    }
    
    // Validar configuración de frequency
    for (const channel of this.channels) {
      if (this.isChannelEnabled(channel)) {
        const channelForm = this.getChannelForm(channel);
        const frequency = channelForm.get('frequency')?.value || 1;
        const interval = channelForm.get('frequencyInterval')?.value || FREQUENCY_CONFIG.DEFAULT_INTERVAL;
        
        if (frequency > 1 && interval < FREQUENCY_CONFIG.MIN_INTERVAL) {
          return `El intervalo mínimo para ${this.channelLabels[channel as keyof typeof this.channelLabels]} debe ser ${FREQUENCY_CONFIG.MIN_INTERVAL} minutos`;
        }
      }
    }
    
    return '';
  }
}
